// 插入文件面板 — 列出本人文件(走 v_file_info_mine,过 view-first lint),多选后
// 单事务批量插入 fileRef 节点(只写 fileId uuid,绝不写 url)。
//
// 数据源 useFilePickList → v_file_info_mine(owner 收口);只挑文件不挑文件夹。

import { useMemo, useState } from 'react'
import { Spinner, formatBytes } from '@dogdisk/shared'
import { useFilePickList, type FileInfoOption } from '../../_lib/pack-api'

type InsertFilePanelProps = {
  open: boolean
  onClose: () => void
  /** 确认后回调选中的文件 id 列表(uuid),由编辑器 insertFileRefs 批量插入。 */
  onConfirm: (fileIds: string[]) => void
}

export function InsertFilePanel({ open, onClose, onConfirm }: InsertFilePanelProps) {
  const { data: files, isLoading } = useFilePickList(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const fileOptions = useMemo(
    () => (files ?? []).filter((f: FileInfoOption) => f.type !== 'folder'),
    [files],
  )

  if (!open) return null

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const confirm = () => {
    onConfirm([...selected])
    setSelected(new Set())
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-black/50"
      data-testid="article-insert-file-panel"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl bg-white"
        style={{ maxHeight: '80vh', paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-2">
          <span className="h-1 w-10 rounded-full bg-[#E0E0E0]" />
        </div>
        <div className="flex items-center justify-between px-6 pb-2">
          <p className="text-[14px] font-semibold text-[#1A1A1A]">插入文件</p>
          <button
            type="button"
            data-testid="article-insert-file-confirm"
            onClick={confirm}
            disabled={selected.size === 0}
            className="text-[14px] text-[#19caad] disabled:opacity-40"
          >
            插入 {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="text-gray-400" />
          </div>
        ) : (
          <ul className="max-h-[60vh] overflow-y-auto px-6 pb-4">
            {fileOptions.map((f) => (
              <li key={f.id}>
                <label className="flex items-center gap-3 rounded-lg p-3 active:bg-[#F6F7F8]">
                  <input
                    type="checkbox"
                    checked={selected.has(f.id)}
                    onChange={() => toggle(f.id)}
                    className="h-4 w-4 accent-[#19caad]"
                  />
                  <span className="flex-1 truncate text-[14px]">{f.name}</span>
                  {f.size != null && (
                    <span className="text-[11px] text-[#9CA3AF]">{formatBytes(f.size)}</span>
                  )}
                </label>
              </li>
            ))}
            {fileOptions.length === 0 && (
              <li className="py-8 text-center text-[12px] text-[#9CA3AF]">暂无文件，先去上传</li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}
